<template>
  <div>
    <v-dialog
      width="440"
      :model-value="openShareDialog"
      @update:model-value="handleToggleDialog"
      @keydown.stop.esc="$emit('close')">
      <div class="dialog">
        <div class="d-flex mb-6 align-center">
          <v-icon size="large" class="mr-3" style="margin-top: 2px;">mds:group_add</v-icon>
          <span class="text-title mr-3 text-truncate">Share '{{ workflow.name }}'</span>
          <v-spacer/>
          <help-dialog-share-workflow #default="{ props }">
            <v-btn
              variant="text"
              size="small"
              icon="mdi-help-circle-outline"
              v-bind="props"
              class="mr-2"
              :disabled="isWorkflowDisabled"
            />
          </help-dialog-share-workflow>
          <v-btn size="x-small" variant="text" color="grey" @click="$emit('close')" icon="mdi-close">
          </v-btn>
        </div>

        <invite-team-members-field v-model="invites" :need-seats="needSeats" />
        <div class="text-subtitle mt-6">
          Members in '{{ project.name }}' with access:
        </div>
        <base-team-member v-for="member in listTeamMembers" :teamMember="member" :project="project" :key="member.id"/>

        <div class="mt-6">
          <span class="text-subtitle">
            Public access:
          </span>
          <v-select
            :disabled="!isShareEnabled || isWorkflowDisabled"
            v-model="accessLevel"
            :items="accessLevelItems"
            item-title="name"
            item-value="value"
            variant="outlined"
            width="100%"
            hide-details
            class="mx-0 mb-0"
            :class="isShareEnabled ? 'access-level-select' : 'access-level-select access-disabled'"
          >
          </v-select>
        </div>

        <div class="mt-6">
          <span class="text-subtitle">
            Embed:
          </span>
          <div v-if="isPublicWorkflow" class="mt-2">
            <v-text-field
              :model-value="embedUrl"
              readonly
              variant="outlined"
              density="compact"
              hide-details
              class="embed-url"
              @focus="$event.target.select()"
            >
              <template #append-inner>
                <v-tooltip location="top" text="Copy link (for Notion, Slack, etc.)">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      variant="text"
                      size="small"
                      icon="mdi-link-variant"
                      @click="copyEmbedUrl"
                    />
                  </template>
                </v-tooltip>
                <v-tooltip location="top" text="Copy iframe code (for a web page)">
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      variant="text"
                      size="small"
                      icon="mdi-code-tags"
                      @click="copyEmbedCode"
                    />
                  </template>
                </v-tooltip>
              </template>
            </v-text-field>
            <v-select
              v-model="embedSize"
              :items="embedSizes"
              item-title="name"
              item-value="value"
              label="Frame size (for iframe code)"
              variant="outlined"
              density="compact"
              hide-details
              class="mt-2"
            />
          </div>
          <div v-else class="embed-hint mt-2">
            Set public access to "Anyone with link can view" to embed this board on a web page.
          </div>
        </div>

        <div class="d-flex justify-space-between mt-6">
          <v-btn variant="tonal" size="large" @click="saveLinkToClipboard">
            <v-icon size="large" class="mr-1">mds:link</v-icon>Copy link
          </v-btn>
          <div class="d-flex ga-2">
            <v-btn variant="outlined" size="large" @click="emit('close')" :disabled="isInviting">
              Close
            </v-btn>
            <v-btn
              variant="flat"
              size="large"
              @click="requestInvite(doInvite)"
              :disabled="!hasValidInvites || isInviting || !canInvite || classifying"
            >
              Invite {{ invites.length > 0 ? `${invites.length} new members` : '' }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-dialog>

    <seat-confirmation-dialog
      :confirmation="confirmation"
      :busy="isInviting"
      @confirm="confirm"
      @cancel="cancel"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import BaseTeamMember from '@/components/BaseTeamMember.vue'
import InviteTeamMembersField from '@/components/InviteTeamMembersField.vue'
import SeatConfirmationDialog from '@/components/SeatConfirmationDialog.vue'
import HelpDialogShareWorkflow from '@/components/HelpDialogShareWorkflow.vue'
import { useInviteTeamMembers } from '@/composables/useInviteTeamMembers'
import { useSeatConfirmation } from '@/composables/useSeatConfirmation'
import { buildEmbedUrl } from '@/utils/embed'
import workflowApi from '@/api/workflowApi'
import { useBrand } from '@/composables/useBrand'

const props = defineProps({
  isShareEnabled: {},
  openShareDialog: {},
  workflow: {}
})

const emit = defineEmits(['close'])
const store = useStore()
const route = useRoute()
const brand = useBrand()

const invites = ref([])
const accessLevelItems = ref([
  {
    name: 'No access',
    value: 'PRIVATE'
  }, {
    name: 'Anyone with link can view',
    value: 'READ'
  }
])

const { inviteTeamMembers, isInviting } = useInviteTeamMembers()
const { classifying, needSeats, canInvite, confirmation, requestInvite, confirm, cancel } = useSeatConfirmation(invites)

const listTeamMembers = computed(() => store.state.teamMembers.teamMembers)
const project = computed(() => store.getters['projectMemberships/activeProject'])
const isWorkflowDisabled = computed(() => store.state.isWorkflowDisabled)
const hasValidInvites = computed(() => invites.value && invites.value.length > 0)

const accessLevel = computed({
  get: () => props.workflow.accessLevel || accessLevelItems.value[0].value,
  set: (value) => {
    workflowApi.updateWorkflowNoHistoryMutation(
      route.params.projectId,
      {
        id: route.params.id,
        expectedVersion: props.workflow.version,
        accessLevel: value
      }
    )
  }
})

const windowLocation = computed(() => window.location)

const isPublicWorkflow = computed(() => accessLevel.value === 'READ')

// Frame-size presets for the iframe snippet. `width: null` means full-width/responsive.
const embedSizes = [
  { name: 'Small (600 × 450)', value: 'small', width: 600, height: 450 },
  { name: 'Medium (800 × 600)', value: 'medium', width: 800, height: 600 },
  { name: 'Large (1000 × 750)', value: 'large', width: 1000, height: 750 },
  { name: 'Responsive (full width)', value: 'responsive', width: null, height: 600 }
]
const embedSize = ref('medium')

const embedUrl = computed(() =>
  buildEmbedUrl({
    origin: window.location.origin,
    projectId: route.params.projectId,
    workflowId: route.params.id
  })
)

const embedSnippet = computed(() => {
  // Escape quotes so a workflow name can't break out of the title attribute.
  const title = (props.workflow?.name || `${brand.name} board`).replace(/"/g, '&quot;')
  const size = embedSizes.find((s) => s.value === embedSize.value) || embedSizes[1]
  const width = size.width ? `${size.width}` : '100%'

  return `<iframe src="${embedUrl.value}" title="${title}" width="${width}" height="${size.height}" loading="lazy" style="border:1px solid #ccc;border-radius:8px" allowfullscreen></iframe>`
})

const copyToClipboard = async (text, successMessage) => {
  try {
    await navigator.clipboard.writeText(text)
    store.commit('snackbar/showMessage', {
      content: successMessage,
      timeout: 3000
    }, { root: true })
  } catch (e) {
    store.commit('snackbar/showMessage', {
      content: 'Could not copy to clipboard — please copy manually.',
      timeout: 3000
    }, { root: true })
  }
}

const copyEmbedUrl = () => copyToClipboard(embedUrl.value, 'Public link copied — paste it into Notion, Slack, etc.')

const copyEmbedCode = () => copyToClipboard(embedSnippet.value, 'Embed code copied — paste it into any web page.')

const doInvite = async () => {
  if (invites.value.length > 0) {
    await inviteTeamMembers(invites.value, props.workflow?.name, needSeats.value)
    invites.value = []
  }
  emit('close')
}

const saveLinkToClipboard = () => {
  navigator.clipboard.writeText(windowLocation.value.toString())
  store.commit('snackbar/showMessage', {
    content: 'Link copied. You can share it with your team!',
    timeout: 3000
  }, { root: true })
}

const handleToggleDialog = (val) => {
  if (!val) {
    emit('close')
  } else {
    invites.value = []
  }
}
</script>

<style scoped>
.dialog {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--light-1);
  background: var(--light-1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.15);
}

.access-level-select {
  border: none;
  outline: none;
  appearance: none;
  margin: 5px;

  max-width: 100%;
  :focus {
    outline:none;
  }
}

.access-level-select option {
  border: none;
  padding: 5px;
  font-size: 16px;
  color: var(--text-dark);
  appearance: none;
}

.access-disabled .v-select__selection  {
  color: grey !important;
}

.embed-url :deep(input) {
  font-size: 13px;
  color: var(--text-light);
}

.embed-hint {
  font-size: 13px;
  color: var(--text-light);
}
</style>
